"use client";

import { useState, useRef, useCallback } from "react";
import { useClientEffect } from "@/hooks/useClientEffect";

export const useIntersection = (threshold: number = 0) => {
  const ref = useRef<HTMLElement | null>(null);
  const [isVisible, setIsVisible] = useState(false);

  const observe = useCallback(() => {
    const el = ref.current;
    if (!el || isVisible) return null;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold },
    );

    observer.observe(el);
    return observer;
  }, [threshold, isVisible]);

  useClientEffect(() => {
    let io = observe();

    const ro =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(() => {
            io?.disconnect();
            io = observe();
          })
        : null;

    if (ref.current) ro?.observe(ref.current);

    return () => {
      io?.disconnect();
      ro?.disconnect();
    };
  }, [observe]);

  return { ref, isVisible };
};
